import {isEmpty} from 'lodash-es';

import {
  AmountInput,
  Button,
  CheckBox,
  Input,
  SpinningLoader,
} from '@/components';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import type {AfriexAdminAmount, AfriexReferralTier} from '@/types';
import {AfriexCurrencyCode} from '@/types';

import {TierActionType} from './types';
import useEventTierUpdate from './useEventTierUpdate';

interface EventTierProps extends Pick<UserModalProps, 'isOpen' | 'onClose'> {
  data?: AfriexReferralTier;
}

const EventTierForm = ({isOpen, onClose, data}: EventTierProps) => {
  const {dispatch, isLoading, handleSubmit, tier} = useEventTierUpdate(
    onClose,
    data,
  );

  const {
    name,
    isDeactivated,
    referralCode,
    bonusAmountPerReferree,
    cumulativeThreshold,
    transactionWindow,
    shouldAutomatePayout,
  } = tier;

  const updateAction = {
    type: TierActionType.UPDATE_TIER,
    payload: {},
  };
  const updateLabel = isEmpty(data)
    ? 'Create New Event Tier +'
    : 'Update Event Tier';
  const submitLabel = isEmpty(data) ? 'Submit' : 'Update';
  const isDisabled = isEmpty(tier);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-96"
      closeOnOutsideClick
      title={updateLabel}
    >
      {isLoading ? (
        <div className="flex w-full h-full justify-center items-center my-5">
          <SpinningLoader />
        </div>
      ) : (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // Fire-and-forget: useEventTierUpdate's handleSubmit
              // try/catches, clears `isLoading` and reports through
              // showErrorMessage / showSuccessMessage, so this only stops an
              // unexpected throw becoming an unhandled rejection.
              handleSubmit(tier).catch(() => {});
            }}
            className="flex flex-col space-y-5"
          >
            <div className="flex flex-col gap-3 px-2">
              <Input
                name="name"
                label="Name"
                type="text"
                value={name || ''}
                className="w-full"
                onChange={(e) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      name: e.target?.value || name,
                    },
                  })
                }
              />

              <AmountInput
                label="Bonus Amount Per Referee (USD)"
                fixedCurrency={AfriexCurrencyCode.USD}
                value={{
                  amount: bonusAmountPerReferree,
                  currency: AfriexCurrencyCode.USD,
                }}
                onChange={(value: AfriexAdminAmount) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      bonusAmountPerReferree: value?.amount
                        ? Number(value?.amount)
                        : bonusAmountPerReferree,
                    },
                  })
                }
              />

              <AmountInput
                fixedCurrency={AfriexCurrencyCode.USD}
                value={{
                  amount: cumulativeThreshold,
                  currency: AfriexCurrencyCode.USD,
                }}
                label="Cumulative Transaction Threshold (USD)"
                onChange={(value: AfriexAdminAmount) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      cumulativeThreshold: value?.amount
                        ? Number(value?.amount)
                        : cumulativeThreshold,
                    },
                  })
                }
              />

              <Input
                name="transactionWindow"
                label="Transaction Window (days)"
                type="number"
                value={transactionWindow}
                className="w-full"
                onChange={(e) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      transactionWindow: Number(e.target?.value),
                    },
                  })
                }
              />

              <CheckBox
                label="Mark to automate payout"
                type="checkbox"
                checked={shouldAutomatePayout}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      shouldAutomatePayout: Boolean(e.target?.checked),
                    },
                  })
                }
              />

              <Input
                name="referralCode"
                label="Referral Code"
                type="text"
                value={referralCode || ''}
                className="w-full"
                onChange={(e) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      referralCode: e.target?.value?.toLowerCase() || '',
                    },
                  })
                }
              />

              <CheckBox
                label="Mark as active"
                type="checkbox"
                checked={!isDeactivated}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      isDeactivated: !e.target?.checked,
                    },
                  })
                }
              />

              <div className="flex w-full justify-end space-x-2 py-3">
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outline"
                  colorScheme="cyan"
                >
                  Cancel
                </Button>

                <Button
                  disabled={isDisabled}
                  type="submit"
                  variant="solid"
                  colorScheme="cyan"
                >
                  {submitLabel}
                </Button>
              </div>
            </div>
          </form>
        </>
      )}
    </Modal>
  );
};

export default EventTierForm;
